<!-- src/views/SafeCountView.vue -->

<template>
  <div class="section coredeck-page-section">
    <div class="row">
      <div class="col s12">
        <ul ref="collapsibleEl" class="collapsible sc-collapsible">
          <li>
            <div class="collapsible-header">
              <i class="material-icons"> savings </i>

              Safe Count Summary
            </div>

            <div class="collapsible-body sc-summary-body">
              <div class="sc-summary-wrap">
                <SafeWeekSummaryCard
                  ref="safeWeekSummaryCardRef"
                  :venue-id="context.venueId"
                  :week-ending-date="context.weekEnding"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <br />

  <div class="section">
    <div class="row">
      <div class="col s12">
        <div class="card sc-card">
          <div class="card-content blue-grey darken-4 white-text sc-card-header">
            <div class="row sc-header-row">
              <div class="col s12 m7">
                <span class="card-title"> Safe Check </span>
              </div>

              <div class="col s12 m5 right-align sc-header-actions">
                <button
                  class="btn-small green darken-1 tooltipped sc-action-btn"
                  :disabled="loading || !ready"
                  @click="submitCheck"
                  data-position="bottom"
                  data-tooltip="Creates a NEW safe check submission."
                >
                  Submit Safe Check
                </button>
              </div>
            </div>
          </div>

          <div class="card-content sc-card-body">
            <div
              v-if="!ready"
              class="card-panel amber lighten-4 brown-text text-darken-4"
            >
              Safe Count is loading venue/date context.

              <div style="margin-top: 8px">
                <strong> Current context: </strong>

                venueId="{{ context.venueId || "—" }}", businessDate="{{
                  context.businessDate || "—"
                }}", weekEnding="{{ context.weekEnding || "—" }}", pettyCash="£{{
                  money(context.pettyCash)
                }}"
              </div>
            </div>

            <template v-else>
              <div
                v-if="context.isFallbackBusinessDate"
                class="card-panel blue lighten-5 blue-text text-darken-4"
              >
                No live trading date was available, so Safe Check loaded using the latest
                known business date:

                <strong>
                  {{ context.businessDate }}
                </strong>
              </div>

              <div class="row">
                <div class="input-field col s12 m6">
                  <input
                    id="openingBalance"
                    type="text"
                    :value="money(state.openingBalance)"
                    readonly
                  />

                  <label class="active" for="openingBalance"> Opening Balance (£) </label>

                  <small class="grey-text">
                    <span v-if="openingSource">
                      {{ openingSource }}
                    </span>

                    <span v-else> Loaded from previous completed safe count </span>
                  </small>
                </div>

                <div class="input-field col s12 m6">
                  <input id="pettyCash" type="text" :value="money(pettyCash)" readonly />

                  <label class="active" for="pettyCash">
                    P.Cash (£) (current + unchecked carry forward)
                  </label>

                  <small class="grey-text">
                    Current week ending
                    {{ context.weekEnding || "—" }}

                    <span v-if="pettyCashCarryCount > 0">
                      • {{ pettyCashCarryCount }} unchecked item{{
                        pettyCashCarryCount === 1 ? "" : "s"
                      }}
                      included
                    </span>
                  </small>
                </div>
              </div>

              <div class="divider"></div>

              <div class="row">
                <div class="col s12">
                  <h5 class="center sc-section-title">Notes</h5>
                </div>
              </div>

              <div class="sc-table-wrap">
                <table class="highlight sc-table">
                  <thead>
                    <tr>
                      <th style="width: 40%">Denomination</th>

                      <th style="width: 30%">Amount (£)</th>

                      <th style="width: 30%">Line Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="row in NOTE_DENOMS" :key="row.key">
                      <td>
                        <strong>
                          {{ row.label }}
                        </strong>
                      </td>

                      <td>
                        <input
                          class="browser-default sc-money-input"
                          type="number"
                          min="0"
                          step="0.01"
                          inputmode="decimal"
                          :value="displayMoneyInput(state.notes[row.key])"
                          @input="onMoneyInput('notes', row.key, $event.target.value)"
                        />
                      </td>

                      <td>£{{ money(state.notes[row.key]) }}</td>
                    </tr>
                  </tbody>

                  <tfoot>
                    <tr>
                      <th colspan="2" class="right-align">Notes Subtotal</th>

                      <th>£{{ money(notesTotal) }}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div class="divider" style="margin: 24px 0"></div>

              <div class="row">
                <div class="col s12">
                  <h5 class="center sc-section-title">Coins</h5>
                </div>
              </div>

              <div class="sc-table-wrap">
                <table class="highlight sc-table">
                  <thead>
                    <tr>
                      <th style="width: 40%">Denomination</th>

                      <th style="width: 30%">Amount (£)</th>

                      <th style="width: 30%">Line Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="row in COIN_DENOMS" :key="row.key">
                      <td>
                        <strong>
                          {{ row.label }}
                        </strong>
                      </td>

                      <td>
                        <input
                          class="browser-default sc-money-input"
                          type="number"
                          min="0"
                          step="0.01"
                          inputmode="decimal"
                          :value="displayMoneyInput(state.coins[row.key])"
                          @input="onMoneyInput('coins', row.key, $event.target.value)"
                        />
                      </td>

                      <td>£{{ money(state.coins[row.key]) }}</td>
                    </tr>
                  </tbody>

                  <tfoot>
                    <tr>
                      <th colspan="2" class="right-align">Coins Subtotal</th>

                      <th>£{{ money(coinsTotal) }}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div class="divider" style="margin: 24px 0"></div>

              <div class="row">
                <div class="col s12">
                  <h5 class="center sc-section-title">Floats</h5>
                </div>
              </div>

              <div class="sc-table-wrap">
                <table class="highlight sc-table">
                  <thead>
                    <tr>
                      <th style="width: 40%">Line</th>

                      <th style="width: 30%">Amount (£)</th>

                      <th style="width: 30%">Line Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>
                        <strong> Floats </strong>
                      </td>

                      <td>
                        <input
                          class="browser-default sc-money-input"
                          type="number"
                          min="0"
                          step="0.01"
                          inputmode="decimal"
                          :value="displayMoneyInput(state.floats)"
                          @input="onFloatsInput($event.target.value)"
                        />
                      </td>

                      <td>£{{ money(state.floats) }}</td>
                    </tr>
                  </tbody>

                  <tfoot>
                    <tr>
                      <th colspan="2" class="right-align">Floats Subtotal</th>

                      <th>£{{ money(floatsTotal) }}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div class="divider" style="margin: 24px 0"></div>

              <div class="row sc-totals-row">
                <div class="col s12 m6">
                  <div class="card-panel sc-total-panel">
                    <div class="row sc-total-line">
                      <div class="col s7">
                        <strong> Notes </strong>
                      </div>

                      <div class="col s5 right-align">£{{ money(notesTotal) }}</div>
                    </div>

                    <div class="row sc-total-line">
                      <div class="col s7">
                        <strong> Coins </strong>
                      </div>

                      <div class="col s5 right-align">£{{ money(coinsTotal) }}</div>
                    </div>

                    <div class="row sc-total-line">
                      <div class="col s7">
                        <strong> Floats </strong>
                      </div>

                      <div class="col s5 right-align">£{{ money(floatsTotal) }}</div>
                    </div>

                    <div class="row sc-total-line">
                      <div class="col s7">
                        <strong> Counted Cash </strong>
                      </div>

                      <div class="col s5 right-align">
                        <strong> £{{ money(countedCash) }} </strong>
                      </div>
                    </div>

                    <div class="row sc-total-line">
                      <div class="col s7">
                        <strong> P.Cash </strong>
                      </div>

                      <div class="col s5 right-align">£{{ money(pettyCash) }}</div>
                    </div>
                  </div>
                </div>

                <div class="col s12 m6">
                  <div class="card-panel blue lighten-4 sc-total-panel">
                    <div class="row sc-total-line">
                      <div class="col s7">
                        <strong> Closing Balance </strong>
                      </div>

                      <div class="col s5 right-align">
                        <strong> £{{ money(closingBalance) }} </strong>
                      </div>
                    </div>

                    <div class="row sc-total-line">
                      <div class="col s7">
                        <strong> Variance </strong>
                      </div>

                      <div class="col s5 right-align" :class="varianceClass">
                        <strong> £{{ money(variance) }} </strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="error" class="card-panel red lighten-4 red-text text-darken-4">
                {{ error }}
              </div>

              <div
                v-if="success"
                class="card-panel green lighten-4 green-text text-darken-4"
              >
                {{ success }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
  nextTick,
} from "vue";

import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";

import SafeWeekSummaryCard from "@/components/dashboard/SafeWeekSummaryCard.vue";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { safeCheckUpsert } from "@/scripts/sqlApiHelper/safeApi";

import { db } from "@/firebase";

const vc = useVenueContext();

const collapsibleEl = ref(null);

const safeWeekSummaryCardRef = ref(null);

let collapsibleInstance = null;

let tooltipInstances = [];

let hydratingContext = false;

const NOTE_DENOMS = [
  {
    key: "n50",
    label: "£50 Notes",
  },
  {
    key: "n20",
    label: "£20 Notes",
  },
  {
    key: "n10",
    label: "£10 Notes",
  },
  {
    key: "n5",
    label: "£5 Notes",
  },
];

const COIN_DENOMS = [
  {
    key: "c2",
    label: "£2 Coins",
  },
  {
    key: "c1",
    label: "£1 Coins",
  },
  {
    key: "c050",
    label: "50p Coins",
  },
  {
    key: "c020",
    label: "20p Coins",
  },
  {
    key: "c010",
    label: "10p Coins",
  },
  {
    key: "c005",
    label: "5p Coins",
  },
  {
    key: "c002",
    label: "2p Coins",
  },
  {
    key: "c001",
    label: "1p Coins",
  },
];

const loading = ref(false);

const error = ref("");

const success = ref("");

const pettyCashCarryCount = ref(0);

const context = reactive({
  venueId: "",
  businessDate: "",
  weekEnding: "",
  pettyCash: 0,
  isFallbackBusinessDate: false,
});

const state = reactive({
  openingBalance: 0,

  notes: Object.fromEntries(NOTE_DENOMS.map((d) => [d.key, null])),

  coins: Object.fromEntries(COIN_DENOMS.map((d) => [d.key, null])),

  floats: null,
});

const openingSource = ref("");

function clean(s) {
  return String(s ?? "").trim();
}

function normalizeDate(s) {
  const v = clean(s);

  if (!v || v === "-" || v === "—") {
    return "";
  }

  return v;
}

function num(v) {
  const n = Number(v);

  return Number.isFinite(n) ? n : 0;
}

function money(v) {
  const n = num(v);

  return new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,

    maximumFractionDigits: 2,
  }).format(n);
}

function displayMoneyInput(v) {
  return v === null || v === undefined || v === "" ? "" : v;
}

function parseMoneyInput(raw) {
  if (raw === null || raw === undefined) {
    return null;
  }

  const trimmed = String(raw).trim();

  if (!trimmed) {
    return null;
  }

  const n = Number(trimmed);

  if (!Number.isFinite(n)) {
    return null;
  }

  return Math.max(0, Math.round(n * 100) / 100);
}

function onMoneyInput(bucket, key, rawValue) {
  state[bucket][key] = parseMoneyInput(rawValue);
}

function onFloatsInput(rawValue) {
  state.floats = parseMoneyInput(rawValue);
}

function resetBreakdown() {
  NOTE_DENOMS.forEach((d) => {
    state.notes[d.key] = null;
  });

  COIN_DENOMS.forEach((d) => {
    state.coins[d.key] = null;
  });

  state.floats = null;
}

function parseISODateLocal(iso) {
  const [y, m, d] = String(iso || "")
    .split("-")
    .map(Number);

  if (!y || !m || !d) {
    return null;
  }

  return new Date(y, m - 1, d);
}

function toISODateLocal(d) {
  const y = d.getFullYear();

  const m = String(d.getMonth() + 1).padStart(2, "0");

  const day = String(d.getDate()).padStart(2, "0");

  return `${y}-${m}-${day}`;
}

function weekEndingFromTradingDate(iso) {
  const d = parseISODateLocal(iso);

  if (!d) return "";

  const copy = new Date(d);

  const day = copy.getDay();

  const daysToSunday = (7 - day) % 7;

  copy.setDate(copy.getDate() + daysToSunday);

  return toISODateLocal(copy);
}

function tradingDateFromWeekEnding(weekEndingIso) {
  const d = parseISODateLocal(weekEndingIso);

  if (!d) return "";

  const copy = new Date(d);

  copy.setDate(copy.getDate() - 6);

  return toISODateLocal(copy);
}

function isoToSortable(iso) {
  const v = clean(iso);

  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "";
}

function timestampToMillis(value) {
  if (!value) {
    return 0;
  }

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  const parsed = Date.parse(String(value));

  return Number.isFinite(parsed) ? parsed : 0;
}

function getVcValue(...keys) {
  for (const key of keys) {
    const maybeRef = vc?.[key];

    const value =
      maybeRef && typeof maybeRef === "object" && "value" in maybeRef
        ? maybeRef.value
        : maybeRef;

    const cleaned = clean(value);

    if (cleaned) {
      return cleaned;
    }
  }

  return "";
}

async function getOutstandingPettyCashUpToWeek(venueId, targetWeekEndingIso) {
  if (!venueId || !targetWeekEndingIso) {
    return {
      total: 0,
      count: 0,
    };
  }

  const weeksRef = collection(db, "venues", venueId, "petty_cash_weeks");

  const weeksSnap = await getDocs(weeksRef);

  const eligibleWeeks = [];

  weeksSnap.forEach((docSnap) => {
    const row = docSnap.data() || {};

    const weekEnding = clean(row.week_ending_date || row.weekEndingDate || docSnap.id);

    if (weekEnding && weekEnding <= targetWeekEndingIso) {
      eligibleWeeks.push({
        id: docSnap.id,

        weekEnding,
      });
    }
  });

  eligibleWeeks.sort((a, b) => a.weekEnding.localeCompare(b.weekEnding));

  let total = 0;

  let count = 0;

  for (const week of eligibleWeeks) {
    const entriesRef = collection(
      db,
      "venues",
      venueId,
      "petty_cash_weeks",
      week.id,
      "petty_cash_entries"
    );

    const entriesSnap = await getDocs(entriesRef);

    entriesSnap.forEach((entrySnap) => {
      const row = entrySnap.data() || {};

      const claimed =
        row.is_claimed_back === true ||
        row.claimed_back === true ||
        row.claimed === true ||
        row.checked_off === true ||
        row.is_checked_off === true;

      if (!claimed) {
        total += num(row.amount);

        count += 1;
      }
    });
  }

  return {
    total: Number(total.toFixed(2)),

    count,
  };
}

async function getLatestSafeCheckTradingDate(venueId) {
  if (!venueId) {
    return "";
  }

  const checksRef = collection(db, "venues", venueId, "safe_checks");

  const q = query(checksRef, orderBy("submitted_at", "desc"), limit(10));

  const snap = await getDocs(q);

  let bestTradingDate = "";

  snap.forEach((docSnap) => {
    const row = docSnap.data() || {};

    const tradingDate = isoToSortable(row.trading_date);

    if (!tradingDate) {
      return;
    }

    if (!bestTradingDate || tradingDate > bestTradingDate) {
      bestTradingDate = tradingDate;
    }
  });

  return bestTradingDate;
}

async function getPreviousSafeCheckClosingBalance(venueId, currentTradingDate) {
  if (!venueId || !currentTradingDate) {
    return 0;
  }

  const currentIso = isoToSortable(currentTradingDate);

  if (!currentIso) {
    return 0;
  }

  const checksRef = collection(db, "venues", venueId, "safe_checks");

  const q = query(checksRef, orderBy("submitted_at", "desc"));

  const snap = await getDocs(q);

  let best = null;

  snap.forEach((docSnap) => {
    const row = docSnap.data() || {};

    const tradingDate = isoToSortable(row.trading_date);

    const closingBalance = row.closing_balance;

    if (!tradingDate) {
      return;
    }

    if (tradingDate >= currentIso) {
      return;
    }

    if (
      closingBalance === null ||
      closingBalance === undefined ||
      closingBalance === ""
    ) {
      return;
    }

    const submittedMillis = timestampToMillis(row.submitted_at);

    const effectiveSort = submittedMillis || Date.parse(`${tradingDate}T23:59:59`) || 0;

    if (!best || effectiveSort > best.effectiveSort) {
      best = {
        effectiveSort,

        closingBalance: num(closingBalance),

        tradingDate,
      };
    }
  });

  return best ? num(best.closingBalance) : 0;
}

async function hydrateContext() {
  if (hydratingContext) {
    return;
  }

  hydratingContext = true;

  try {
    const venueId = getVcValue("venueId") || clean(localStorage.getItem("venue"));

    let businessDate =
      getVcValue("tradingDate", "businessDate") ||
      clean(localStorage.getItem("tradingDate")) ||
      clean(localStorage.getItem("businessDate"));

    let weekEnding =
      getVcValue("weekEnding", "weekEndingDate") ||
      clean(localStorage.getItem("weekEnding")) ||
      weekEndingFromTradingDate(businessDate);

    let isFallbackBusinessDate = false;

    if (venueId && !businessDate) {
      const latestSafeDate = await getLatestSafeCheckTradingDate(venueId);

      if (latestSafeDate) {
        businessDate = latestSafeDate;

        isFallbackBusinessDate = true;
      }
    }

    if (!businessDate && weekEnding) {
      businessDate = tradingDateFromWeekEnding(weekEnding);

      isFallbackBusinessDate = true;
    }

    if (!weekEnding && businessDate) {
      weekEnding = weekEndingFromTradingDate(businessDate);
    }

    context.venueId = normalizeDate(venueId);

    context.businessDate = normalizeDate(businessDate);

    context.weekEnding = normalizeDate(weekEnding);

    context.isFallbackBusinessDate = Boolean(
      context.businessDate && isFallbackBusinessDate
    );

    if (context.venueId) {
      localStorage.setItem("venue", context.venueId);
    }

    if (context.businessDate) {
      localStorage.setItem("tradingDate", context.businessDate);

      localStorage.setItem("businessDate", context.businessDate);
    }

    if (context.weekEnding) {
      localStorage.setItem("weekEnding", context.weekEnding);
    }
  } finally {
    hydratingContext = false;
  }
}

const ready = computed(() =>
  Boolean(context.venueId && context.businessDate && context.weekEnding)
);

const pettyCash = computed(() => num(context.pettyCash));

const notesTotal = computed(() =>
  NOTE_DENOMS.reduce(
    (s, d) => s + num(state.notes[d.key]),

    0
  )
);

const coinsTotal = computed(() =>
  COIN_DENOMS.reduce(
    (s, d) => s + num(state.coins[d.key]),

    0
  )
);

const floatsTotal = computed(() => num(state.floats));

const countedCash = computed(
  () => notesTotal.value + coinsTotal.value + floatsTotal.value
);

const closingBalance = computed(() => countedCash.value + pettyCash.value);

const variance = computed(() => closingBalance.value - num(state.openingBalance));

const varianceClass = computed(() => {
  const v = variance.value;

  if (Math.abs(v) < 0.005) {
    return "green-text text-darken-2";
  }

  return v > 0 ? "orange-text text-darken-2" : "red-text text-darken-2";
});

async function refreshPettyCashFromFirestore() {
  if (!context.venueId || !context.weekEnding) {
    context.pettyCash = 0;

    pettyCashCarryCount.value = 0;

    localStorage.setItem("pettyCash", "0");

    return;
  }

  const venueId = clean(context.venueId);

  const currentWeekEnding = clean(context.weekEnding);

  const { total, count } = await getOutstandingPettyCashUpToWeek(
    venueId,
    currentWeekEnding
  );

  context.pettyCash = Number(total.toFixed(2));

  pettyCashCarryCount.value = count;

  localStorage.setItem("weekEnding", currentWeekEnding);

  localStorage.setItem("pettyCash", String(context.pettyCash));

  localStorage.setItem("pettyCashWeekId", currentWeekEnding);
}

async function refreshSummaryCard() {
  try {
    await safeWeekSummaryCardRef.value?.refresh?.();
  } catch {}
}

function showToast(message, classes = "") {
  try {
    if (window.M?.toast) {
      window.M.toast({
        html: message,

        classes,

        displayLength: 2500,
      });
    }
  } catch {}
}

async function loadLatest() {
  success.value = "";
  error.value = "";

  await hydrateContext();

  if (!ready.value) {
    return;
  }

  loading.value = true;

  try {
    await refreshPettyCashFromFirestore();

    const previousClosingBalance = await getPreviousSafeCheckClosingBalance(
      context.venueId,
      context.businessDate
    );

    state.openingBalance = num(previousClosingBalance);

    openingSource.value = "Loaded from previous completed safe count closing balance";

    resetBreakdown();
  } catch (e) {
    error.value = e?.message || String(e);
  } finally {
    loading.value = false;
  }
}

async function submitCheck() {
  success.value = "";
  error.value = "";

  await hydrateContext();

  if (!ready.value) {
    return;
  }

  loading.value = true;

  try {
    await refreshPettyCashFromFirestore();

    const payload = {
      venue_id: context.venueId,

      trading_date: context.businessDate,

      week_ending_date: context.weekEnding,

      opening_balance: num(state.openingBalance),

      petty_cash: pettyCash.value,

      counted_cash: countedCash.value,

      closing_balance: closingBalance.value,

      variance: variance.value,

      floats: floatsTotal.value,

      notes_breakdown: Object.fromEntries(
        NOTE_DENOMS.map((d) => [d.key, num(state.notes[d.key])])
      ),

      coins_breakdown: Object.fromEntries(
        COIN_DENOMS.map((d) => [d.key, num(state.coins[d.key])])
      ),
    };

    await safeCheckUpsert(payload);

    success.value = "Safe check submitted.";

    showToast("Safe check submitted", "green darken-1");

    context.isFallbackBusinessDate = false;

    await loadLatest();

    await refreshSummaryCard();

    setTimeout(() => {
      success.value = "";
    }, 1500);
  } catch (e) {
    error.value = e?.message || String(e);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await nextTick();

  if (collapsibleEl.value && window.M?.Collapsible) {
    if (collapsibleInstance?.destroy) {
      collapsibleInstance.destroy();
    }

    collapsibleInstance = window.M.Collapsible.init(collapsibleEl.value, {
      accordion: false,
    });
  }

  if (window.M?.Tooltip) {
    tooltipInstances = window.M.Tooltip.init(
      document.querySelectorAll(".tooltipped"),

      {
        enterDelay: 150,

        exitDelay: 50,

        margin: 8,
      }
    );
  }

  await hydrateContext();

  if (ready.value) {
    await loadLatest();

    await refreshSummaryCard();
  }
});

onBeforeUnmount(() => {
  if (collapsibleInstance?.destroy) {
    collapsibleInstance.destroy();
  }

  try {
    if (Array.isArray(tooltipInstances)) {
      tooltipInstances.forEach((t) => t?.destroy?.());
    } else {
      tooltipInstances?.destroy?.();
    }
  } catch {}
});

watch(
  () => [
    vc?.venueId?.value,
    vc?.tradingDate?.value,
    vc?.businessDate?.value,
    vc?.weekEnding?.value,
    vc?.weekEndingDate?.value,
  ],

  async () => {
    const before = JSON.stringify({
      venueId: context.venueId,

      businessDate: context.businessDate,

      weekEnding: context.weekEnding,

      isFallbackBusinessDate: context.isFallbackBusinessDate,
    });

    await hydrateContext();

    const after = JSON.stringify({
      venueId: context.venueId,

      businessDate: context.businessDate,

      weekEnding: context.weekEnding,

      isFallbackBusinessDate: context.isFallbackBusinessDate,
    });

    if (before !== after && ready.value && !loading.value) {
      await loadLatest();

      await refreshSummaryCard();
    }
  }
);
</script>

<style scoped>
input[type="number"],
input[type="text"] {
  margin-top: 6px;
}

.sc-card {
  overflow: hidden;
}

.sc-card-header {
  padding-bottom: 16px !important;
}

.sc-header-row {
  margin-bottom: 0;
}

.sc-header-actions {
  margin-top: 6px;
}

.sc-action-btn {
  margin-left: 8px;
}

.sc-card-body {
  overflow-x: hidden;
}

.sc-section-title {
  margin: 6px 0 12px;
}

.sc-table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.sc-table {
  min-width: 520px;
}

.sc-money-input {
  width: 100%;
  min-width: 90px;
  box-sizing: border-box;
}

.sc-totals-row {
  margin-bottom: 0;
}

.sc-total-panel {
  margin-bottom: 0;
}

.sc-total-line {
  margin-bottom: 0 !important;
}

.sc-summary-body {
  padding: 0;
}

.sc-summary-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 0;
}

.sc-summary-wrap :deep(table) {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
}

.sc-summary-wrap :deep(th),
.sc-summary-wrap :deep(td) {
  white-space: nowrap;
}

.sc-summary-wrap :deep(.card) {
  width: 100%;
}

@media (max-width: 992px) {
  .sc-header-actions {
    text-align: left !important;
    margin-top: 16px;
  }

  .sc-action-btn {
    margin-left: 0 !important;
    margin-right: 8px;
    margin-bottom: 8px;
  }
}

@media (max-width: 600px) {
  .sc-card-header {
    padding: 18px 14px !important;
  }

  .sc-card-body {
    padding: 14px !important;
  }

  .sc-header-actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    text-align: left !important;
  }

  .sc-action-btn {
    width: 100%;
    margin: 0 !important;
    height: 42px;
    line-height: 42px;
  }

  .sc-section-title {
    font-size: 1.4rem;
    margin-bottom: 10px;
  }

  .sc-table {
    min-width: 480px;
  }

  .sc-table th,
  .sc-table td {
    font-size: 12px;
    padding: 8px 10px;
    white-space: nowrap;
  }

  .sc-money-input {
    min-width: 84px;
    height: 36px;
  }

  .sc-total-panel {
    padding: 14px !important;
  }

  .sc-total-line .right-align {
    text-align: left !important;
    margin-top: 4px;
  }

  .sc-summary-wrap {
    padding: 0;
  }

  .sc-summary-wrap :deep(.card-content) {
    padding: 12px !important;
  }

  .sc-summary-wrap :deep(th),
  .sc-summary-wrap :deep(td) {
    font-size: 12px;
    padding: 8px 10px;
  }

  .sc-summary-wrap :deep(.right-align) {
    text-align: left !important;
  }
}
</style>
